import { getApp, getApps, initializeApp } from "firebase/app";
import {
  RecaptchaVerifier,
  getAuth,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";

export const FIREBASE_EMAIL_STORAGE_KEY = "firebaseEmailForSignIn";

export class FirebaseClientConfigurationError extends Error {
  constructor() {
    super("Firebase login is not configured for this website.");
    this.name = "FirebaseClientConfigurationError";
    this.code = "firebase/configuration-missing";
  }
}

let firebaseAuthInstance;

const getFirebaseConfig = () => {
  const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  };

  if (!config.apiKey || !config.authDomain || !config.projectId || !config.appId) {
    throw new FirebaseClientConfigurationError();
  }

  return config;
};

export const getFirebaseAuth = () => {
  if (firebaseAuthInstance) return firebaseAuthInstance;

  const app = getApps().length
    ? getApp()
    : initializeApp(getFirebaseConfig());
  firebaseAuthInstance = getAuth(app);
  return firebaseAuthInstance;
};

export const createPhoneRecaptcha = (
  containerId,
  language = "en",
) => {
  const auth = getFirebaseAuth();
  auth.languageCode = language === "hi" ? "hi" : "en";
  return new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
  });
};

export const requestFirebasePhoneOtp = (
  phoneNumber,
  recaptchaVerifier,
) => signInWithPhoneNumber(getFirebaseAuth(), phoneNumber, recaptchaVerifier);

export const resetPhoneRecaptcha = async (recaptchaVerifier) => {
  if (!recaptchaVerifier || typeof recaptchaVerifier.render !== "function") {
    return;
  }

  const widgetId = await recaptchaVerifier.render();
  const recaptchaApi =
    typeof window === "undefined" ? undefined : window.grecaptcha;

  if (typeof recaptchaApi?.reset !== "function") {
    const error = new Error("The reCAPTCHA widget is not ready.");
    error.code = "auth/captcha-check-failed";
    throw error;
  }

  recaptchaApi.reset(widgetId);
};

export const safelyClearPhoneRecaptcha = (recaptchaVerifier) => {
  if (!recaptchaVerifier || typeof recaptchaVerifier.clear !== "function") {
    return;
  }

  try {
    recaptchaVerifier.clear();
  } catch {
    // Firebase throws if a verifier was already destroyed. Cleanup is idempotent.
  }
};

export const confirmFirebasePhoneOtp = async (confirmationResult, code) => {
  const credential = await confirmationResult.confirm(code);
  return credential.user.getIdToken(true);
};

const emailLinkReturnUrl = () => {
  const publicPath = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return new URL(`${publicPath}/login`, window.location.origin).toString();
};

export const sendFirebaseEmailLink = async (email) => {
  await sendSignInLinkToEmail(getFirebaseAuth(), email, {
    url: emailLinkReturnUrl(),
    handleCodeInApp: true,
  });
  window.localStorage.setItem(FIREBASE_EMAIL_STORAGE_KEY, email);
};

export const getStoredFirebaseEmail = () =>
  window.localStorage.getItem(FIREBASE_EMAIL_STORAGE_KEY) || "";

export const isFirebaseEmailSignInLink = (url = window.location.href) => {
  let parsedUrl;
  try {
    parsedUrl = new URL(url);
  } catch {
    return false;
  }

  if (
    parsedUrl.searchParams.get("mode") !== "signIn" ||
    !parsedUrl.searchParams.get("oobCode")
  ) {
    return false;
  }

  try {
    return isSignInWithEmailLink(getFirebaseAuth(), url);
  } catch (error) {
    if (error instanceof FirebaseClientConfigurationError) return true;
    throw error;
  }
};

export const completeFirebaseEmailLink = async (email, url) => {
  const credential = await signInWithEmailLink(
    getFirebaseAuth(),
    email,
    url,
  );
  window.localStorage.removeItem(FIREBASE_EMAIL_STORAGE_KEY);
  return credential.user.getIdToken(true);
};

export const clearFirebaseSession = async () => {
  if (!firebaseAuthInstance?.currentUser) return;
  await signOut(firebaseAuthInstance);
};
